import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/shared/PageHeader';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { cn } from '@/lib/utils';
import {
    Shield, AlertTriangle, CheckCircle, XCircle, Eye, EyeOff,
    RefreshCw, ChevronDown, Trash2, MessageSquare,
    Camera, Flag, Clock, TrendingUp,
    AlertOctagon, User, ImageOff,
    MapPin, Megaphone, Info,
} from 'lucide-react';
import { dbClient } from '@/services/db-client';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

// ─── CDN ────────────────────────────────────────────────────────────────────

const CDN_BASE_URL = 'https://aipeces-media-a4hsbvf5gff3b6b0.z02.azurefd.net';

function resolveImageUrl(imagePath: string | null | undefined): string | null {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) return imagePath;
    if (imagePath.startsWith('data:')) return imagePath; // base64
    return `${CDN_BASE_URL}/${imagePath}`;
}

// ─── Types ──────────────────────────────────────────────────────────────────

interface EnrichedContentReport {
    id: string;
    content_type: 'capture' | 'moment' | 'comment_capture' | 'comment_material' | 'comment_moment';
    content_id: string;
    reporter_id: string;
    reason: 'offensive' | 'not_content' | 'spam' | 'harassment' | 'other';
    description: string | null;
    status: 'pending' | 'reviewing' | 'resolved' | 'dismissed';
    admin_notes: string | null;
    created_at: string;
    updated_at: string;
    // Reporter info
    reporter_display_name: string | null;
    reporter_email: string | null;
    reporter_avatar_url: string | null;
    // Content author info
    content_author_id: string | null;
    content_author_name: string | null;
    content_author_avatar: string | null;
    // Content preview
    content_image: string | null;
    content_text: string | null;
    content_species: string | null;
    content_location: string | null;
    // Meta
    total_reports_for_content: number;
    content_exists: boolean;
}

interface ModerationStats {
    total_reports: number;
    pending_count: number;
    reviewing_count: number;
    resolved_count: number;
    dismissed_count: number;
    today_count: number;
    this_week_count: number;
    by_content_type: Record<string, number> | null;
    by_reason: Record<string, number> | null;
    top_reporters: Array<{
        reporter_id: string;
        display_name: string | null;
        email: string | null;
        report_count: number;
    }>;
}

type StatusFilter = 'all' | 'pending' | 'reviewing' | 'resolved' | 'dismissed';
type ModerationView = 'reports' | 'community';

interface CommunityHiddenItem {
    content_type: 'capture' | 'moment';
    content_id: string;
    owner_id: string;
    owner_display_name: string | null;
    preview_image: string | null;
    preview_text: string | null;
    likes_count: number;
    dislikes_count: number;
    impressions_count: number;
    dislike_ratio: number;
    community_hidden_at: string | null;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const REASON_LABELS: Record<string, string> = {
    offensive: 'Contenido ofensivo',
    not_content: 'No es contenido de pesca',
    spam: 'Spam',
    harassment: 'Acoso',
    other: 'Otro',
};

const REASON_COLORS: Record<string, string> = {
    offensive: 'bg-red-500/10 text-red-400 border-red-500/20',
    not_content: 'bg-orange-500/10 text-orange-400 border-orange-500/20',
    spam: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    harassment: 'bg-rose-500/10 text-rose-400 border-rose-500/20',
    other: 'bg-slate-500/10 text-slate-400 border-slate-500/20',
};

const CONTENT_TYPE_LABELS: Record<string, string> = {
    capture: 'Captura',
    moment: 'Momento',
    comment_capture: 'Comentario (captura)',
    comment_material: 'Comentario (material)',
    comment_moment: 'Comentario (momento)',
};

const CONTENT_TYPE_ICONS: Record<string, typeof Camera> = {
    capture: Camera,
    moment: Flag,
    comment_capture: MessageSquare,
    comment_material: MessageSquare,
    comment_moment: MessageSquare,
};

const STATUS_CONFIG: Record<string, { variant: 'success' | 'warning' | 'error' | 'info' | 'neutral'; label: string }> = {
    pending: { variant: 'warning', label: 'Pendiente' },
    reviewing: { variant: 'info', label: 'En Revisión' },
    resolved: { variant: 'success', label: 'Resuelto' },
    dismissed: { variant: 'neutral', label: 'Descartado' },
};

// ─── Data Hooks ─────────────────────────────────────────────────────────────

function useModerationStats() {
    return useQuery<ModerationStats>({
        queryKey: ['admin', 'moderation', 'stats'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_moderation_stats');
            if (error) throw error;
            return data as ModerationStats;
        },
        staleTime: 30_000,
    });
}

function useEnrichedReports(statusFilter: StatusFilter) {
    return useQuery<EnrichedContentReport[]>({
        queryKey: ['admin', 'reports', 'enriched', statusFilter],
        queryFn: async () => {
            const params: Record<string, unknown> = {};
            if (statusFilter !== 'all') {
                params.p_status_filter = statusFilter;
            }
            const { data, error } = await dbClient.rpc('admin_get_enriched_content_reports', params);
            if (error) throw error;
            // This RPC returns JSON directly (not a table), so it's already an array
            if (Array.isArray(data)) return data as EnrichedContentReport[];
            return [];
        },
        staleTime: 30_000,
    });
}

function useCommunityHiddenContent() {
    return useQuery<CommunityHiddenItem[]>({
        queryKey: ['admin', 'moderation', 'community-hidden'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_get_community_hidden_content', {
                p_limit: 100,
                p_offset: 0,
            });
            if (error) throw error;
            return (Array.isArray(data) ? data : []) as CommunityHiddenItem[];
        },
        staleTime: 30_000,
    });
}

function useRestoreCommunityHidden() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ contentType, contentId }: { contentType: string; contentId: string }) => {
            const { data, error } = await dbClient.rpc('admin_restore_community_hidden_content', {
                p_content_type: contentType,
                p_content_id: contentId,
            });
            if (error) throw error;
            if (!data) throw new Error('No se pudo restaurar el contenido');
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'moderation', 'community-hidden'] });
        },
    });
}

function useDismissReport() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ reportId, notes }: { reportId: string; notes?: string }) => {
            const { error } = await dbClient.rpc('admin_update_report_status', {
                p_report_id: reportId,
                p_new_status: 'dismissed',
                p_admin_notes: notes || null,
            });
            if (error) throw error;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'reports'] });
            qc.invalidateQueries({ queryKey: ['admin', 'moderation', 'stats'] });
        },
    });
}

function useResolveReport() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ reportId, contentType, contentId }: {
            reportId: string;
            contentType: string;
            contentId: string;
        }) => {
            // Delete the reported content
            const { error: deleteError } = await dbClient.rpc('admin_delete_content', {
                p_content_type: contentType,
                p_content_id: contentId,
            });
            if (deleteError) throw deleteError;

            // Mark report as resolved
            const { error: statusError } = await dbClient.rpc('admin_update_report_status', {
                p_report_id: reportId,
                p_new_status: 'resolved',
            });
            if (statusError) throw statusError;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'reports'] });
            qc.invalidateQueries({ queryKey: ['admin', 'moderation', 'stats'] });
        },
    });
}

function useResolveAllPending() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (notes?: string) => {
            const { data, error } = await dbClient.rpc('admin_resolve_all_pending_reports', {
                p_admin_notes: notes || null,
            });
            if (error) throw error;
            const count = Array.isArray(data) ? data[0] : data;
            return typeof count === 'number' ? count : Number(count) || 0;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['admin', 'reports'] });
            qc.invalidateQueries({ queryKey: ['admin', 'moderation', 'stats'] });
        },
    });
}

// ─── Stats Cards ────────────────────────────────────────────────────────────

function StatsGrid({ stats }: { stats: ModerationStats | undefined }) {
    if (!stats) return null;

    const cards = [
        {
            label: 'Pendientes',
            value: stats.pending_count,
            icon: AlertOctagon,
            color: 'text-amber-400',
            bgColor: 'bg-amber-500/10',
            borderColor: 'border-amber-500/30',
            highlight: stats.pending_count > 0,
        },
        {
            label: 'Hoy',
            value: stats.today_count,
            icon: Clock,
            color: 'text-blue-400',
            bgColor: 'bg-blue-500/10',
            borderColor: 'border-blue-500/20',
        },
        {
            label: 'Esta semana',
            value: stats.this_week_count,
            icon: TrendingUp,
            color: 'text-purple-400',
            bgColor: 'bg-purple-500/10',
            borderColor: 'border-purple-500/20',
        },
        {
            label: 'Total resueltos',
            value: stats.resolved_count,
            icon: CheckCircle,
            color: 'text-emerald-400',
            bgColor: 'bg-emerald-500/10',
            borderColor: 'border-emerald-500/20',
        },
    ];

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {cards.map((card) => {
                const Icon = card.icon;
                return (
                    <div
                        key={card.label}
                        className={cn(
                            'rounded-xl border bg-card p-4 transition-all',
                            card.highlight
                                ? 'border-amber-500/30 shadow-sm shadow-amber-500/5'
                                : 'border-border'
                        )}
                    >
                        <div className="flex items-center gap-3">
                            <div className={cn('p-2 rounded-lg shrink-0', card.bgColor)}>
                                <Icon size={16} className={card.color} />
                            </div>
                            <div className="min-w-0">
                                <p className="text-2xl font-bold text-foreground leading-none">
                                    {card.value}
                                </p>
                                <p className="text-xs text-muted-foreground mt-0.5">{card.label}</p>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

// ─── Confirm Dialog ─────────────────────────────────────────────────────────

function ConfirmAction({
    title,
    description,
    confirmLabel,
    confirmVariant = 'danger',
    onConfirm,
    onCancel,
    isLoading,
    showNotesInput,
    notes,
    onNotesChange,
}: {
    title: string;
    description: string;
    confirmLabel: string;
    confirmVariant?: 'danger' | 'neutral';
    onConfirm: () => void;
    onCancel: () => void;
    isLoading: boolean;
    showNotesInput?: boolean;
    notes?: string;
    onNotesChange?: (notes: string) => void;
}) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 animate-in fade-in">
            <div className="bg-card border border-border rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 animate-in zoom-in-95">
                <h3 className="text-base font-semibold text-foreground mb-1">{title}</h3>
                <p className="text-sm text-muted-foreground mb-4">{description}</p>

                {showNotesInput && (
                    <div className="mb-4">
                        <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1.5">
                            Notas del administrador (opcional)
                        </label>
                        <textarea
                            value={notes}
                            onChange={(e) => onNotesChange?.(e.target.value)}
                            placeholder="Añade una nota sobre esta decisión..."
                            rows={3}
                            className="w-full rounded-lg border border-border bg-muted/30 px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/30 resize-none"
                        />
                    </div>
                )}

                <div className="flex items-center gap-2 justify-end">
                    <button
                        onClick={onCancel}
                        disabled={isLoading}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors disabled:opacity-50"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isLoading}
                        className={cn(
                            'px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50',
                            confirmVariant === 'danger'
                                ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20'
                                : 'bg-muted/50 text-foreground hover:bg-muted'
                        )}
                    >
                        {isLoading ? (
                            <RefreshCw size={14} className="animate-spin" />
                        ) : (
                            confirmLabel
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

// ─── Content Preview Component ──────────────────────────────────────────────

function ContentPreview({ report }: { report: EnrichedContentReport }) {
    const imageUrl = resolveImageUrl(report.content_image);
    const isComment = report.content_type.startsWith('comment_');

    if (!report.content_exists) {
        return (
            <div className="rounded-lg bg-muted/20 border border-dashed border-border p-4 flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted/50">
                    <ImageOff size={20} className="text-muted-foreground/50" />
                </div>
                <div>
                    <p className="text-sm font-medium text-muted-foreground">Contenido eliminado</p>
                    <p className="text-xs text-muted-foreground/60">
                        Este contenido ya no existe en la plataforma
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="rounded-lg border border-border bg-muted/10 overflow-hidden">
            {/* Image preview for captures/moments */}
            {imageUrl && !isComment && (
                <div className="relative aspect-video bg-black/20 max-h-64 overflow-hidden">
                    <img
                        src={imageUrl}
                        alt="Contenido reportado"
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                        }}
                    />
                    {/* Overlay badge */}
                    <div className="absolute top-2 left-2">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-medium backdrop-blur-sm">
                            {report.content_type === 'capture' ? (
                                <><Camera size={10} /> Captura</>
                            ) : (
                                <><Flag size={10} /> Momento</>
                            )}
                        </span>
                    </div>
                </div>
            )}

            {/* Content text */}
            <div className="p-3 space-y-2">
                {/* Species & Location for captures */}
                {report.content_type === 'capture' && (report.content_species || report.content_location) && (
                    <div className="flex flex-wrap items-center gap-2">
                        {report.content_species && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-blue-500/10 text-blue-400 text-xs font-medium">
                                🐟 {report.content_species}
                            </span>
                        )}
                        {report.content_location && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-400 text-xs font-medium">
                                <MapPin size={10} /> {report.content_location}
                            </span>
                        )}
                    </div>
                )}

                {/* Text content */}
                {report.content_text && (
                    <div className={cn(
                        'rounded-md p-2.5',
                        isComment ? 'bg-muted/30 border border-border/50' : ''
                    )}>
                        {isComment && (
                            <p className="text-[10px] font-semibold text-muted-foreground/60 uppercase tracking-wider mb-1">
                                Comentario reportado
                            </p>
                        )}
                        <p className="text-sm text-foreground leading-relaxed">
                            {isComment ? `"${report.content_text}"` : report.content_text}
                        </p>
                    </div>
                )}

                {/* Fallback when no text */}
                {!report.content_text && !imageUrl && (
                    <p className="text-xs text-muted-foreground/50 italic">
                        Sin vista previa disponible
                    </p>
                )}
            </div>
        </div>
    );
}

// ─── User Info Component ────────────────────────────────────────────────────

function UserInfo({ label, name, email, avatarUrl, userId, variant = 'default' }: {
    label: string;
    name: string | null;
    email?: string | null;
    avatarUrl?: string | null;
    userId?: string | null;
    variant?: 'default' | 'reporter' | 'author';
}) {
    const resolvedAvatar = resolveImageUrl(avatarUrl);

    return (
        <div className="flex items-start gap-2.5">
            {/* Avatar */}
            <div className={cn(
                'w-8 h-8 rounded-full shrink-0 flex items-center justify-center overflow-hidden',
                variant === 'reporter' ? 'bg-amber-500/10' :
                    variant === 'author' ? 'bg-blue-500/10' :
                        'bg-muted/50'
            )}>
                {resolvedAvatar ? (
                    <img src={resolvedAvatar} alt={name || ''} className="w-full h-full object-cover" />
                ) : (
                    <User size={14} className={cn(
                        variant === 'reporter' ? 'text-amber-400' :
                            variant === 'author' ? 'text-blue-400' :
                                'text-muted-foreground'
                    )} />
                )}
            </div>
            {/* Info */}
            <div className="min-w-0 flex-1">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                    {label}
                </p>
                <p className="text-sm font-medium text-foreground truncate">
                    {name || 'Desconocido'}
                </p>
                {email && (
                    <p className="text-xs text-muted-foreground truncate">{email}</p>
                )}
                {userId && (
                    <p className="text-[10px] text-muted-foreground/40 font-mono truncate mt-0.5">
                        {userId.substring(0, 12)}...
                    </p>
                )}
            </div>
        </div>
    );
}

// ─── Component ──────────────────────────────────────────────────────────────

export function ModerationPage() {
    const [viewMode, setViewMode] = useState<ModerationView>('reports');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [confirmAction, setConfirmAction] = useState<{
        type: 'dismiss' | 'resolve';
        report: EnrichedContentReport;
    } | null>(null);
    const [confirmResolveAll, setConfirmResolveAll] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');

    const { data: stats, isLoading: isLoadingStats } = useModerationStats();
    const { data: reports, isLoading, refetch } = useEnrichedReports(statusFilter);
    const { data: communityHidden, isLoading: isLoadingCommunity, refetch: refetchCommunity } = useCommunityHiddenContent();
    const dismissMutation = useDismissReport();
    const resolveMutation = useResolveReport();
    const resolveAllMutation = useResolveAllPending();
    const restoreCommunityMutation = useRestoreCommunityHidden();

    const pendingCount = stats?.pending_count || 0;

    const STATUS_FILTERS: { value: StatusFilter; label: string; count?: number }[] = [
        { value: 'pending', label: 'Pendientes', count: pendingCount },
        { value: 'reviewing', label: 'En Revisión' },
        { value: 'resolved', label: 'Resueltos' },
        { value: 'dismissed', label: 'Descartados' },
        { value: 'all', label: 'Todos' },
    ];

    const handleDismiss = (report: EnrichedContentReport) => {
        setAdminNotes('');
        setConfirmAction({ type: 'dismiss', report });
    };

    const handleResolve = (report: EnrichedContentReport) => {
        setAdminNotes('');
        setConfirmAction({ type: 'resolve', report });
    };

    const executeConfirmAction = () => {
        if (!confirmAction) return;

        if (confirmAction.type === 'dismiss') {
            dismissMutation.mutate(
                { reportId: confirmAction.report.id, notes: adminNotes || undefined },
                { onSuccess: () => setConfirmAction(null) }
            );
        } else {
            resolveMutation.mutate(
                {
                    reportId: confirmAction.report.id,
                    contentType: confirmAction.report.content_type,
                    contentId: confirmAction.report.content_id,
                },
                { onSuccess: () => setConfirmAction(null) }
            );
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Moderación"
                description={viewMode === 'reports' ? 'Gestión de reportes de contenido' : 'Contenido ocultado por valoración comunitaria'}
            >
                <button
                    onClick={() => (viewMode === 'reports' ? refetch() : refetchCommunity())}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground bg-muted/50 hover:bg-muted transition-colors"
                >
                    <RefreshCw size={14} />
                    Actualizar
                </button>
            </PageHeader>

            <div className="flex gap-1 p-1 bg-muted/50 rounded-lg w-fit">
                <button
                    onClick={() => setViewMode('reports')}
                    className={cn(
                        'px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                        viewMode === 'reports'
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                    )}
                >
                    Reportes
                </button>
                <button
                    onClick={() => setViewMode('community')}
                    className={cn(
                        'px-4 py-1.5 rounded-md text-sm font-medium transition-all',
                        viewMode === 'community'
                            ? 'bg-primary text-primary-foreground shadow-sm'
                            : 'text-muted-foreground hover:text-foreground'
                    )}
                >
                    Ocultado por comunidad
                    {(communityHidden?.length ?? 0) > 0 && (
                        <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-400">
                            {communityHidden!.length}
                        </span>
                    )}
                </button>
            </div>

            {viewMode === 'community' ? (
                isLoadingCommunity ? (
                    <div className="space-y-4">
                        {Array.from({ length: 3 }).map((_, i) => (
                            <div key={i} className="h-24 rounded-xl border border-border bg-card animate-pulse" />
                        ))}
                    </div>
                ) : !communityHidden || communityHidden.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 text-center">
                        <EyeOff size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                        <p className="text-sm font-medium text-muted-foreground">Sin ocultaciones comunitarias</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {communityHidden.map(item => {
                            const imageUrl = resolveImageUrl(item.preview_image);
                            const TypeIcon = item.content_type === 'capture' ? Camera : Megaphone;
                            return (
                                <div key={`${item.content_type}-${item.content_id}`} className="rounded-xl border border-border bg-card p-4 flex gap-4">
                                    <div className="w-16 h-16 rounded-lg bg-muted/50 overflow-hidden flex-shrink-0 flex items-center justify-center">
                                        {imageUrl ? (
                                            <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                                        ) : (
                                            <ImageOff className="w-6 h-6 text-muted-foreground/50" />
                                        )}
                                    </div>
                                    <div className="flex-1 min-w-0 space-y-1">
                                        <div className="flex items-center gap-2">
                                            <TypeIcon className="w-4 h-4 text-muted-foreground" />
                                            <span className="text-sm font-medium capitalize">{item.content_type === 'capture' ? 'Captura' : 'Publicación'}</span>
                                            <span className="text-xs text-muted-foreground">
                                                {item.community_hidden_at
                                                    ? formatDistanceToNow(new Date(item.community_hidden_at), { addSuffix: true, locale: es })
                                                    : ''}
                                            </span>
                                        </div>
                                        <p className="text-sm truncate">{item.preview_text || '—'}</p>
                                        <p className="text-xs text-muted-foreground">
                                            {item.owner_display_name || 'Usuario'} · 👍 {item.likes_count} · 👎 {item.dislikes_count} · 👁 {item.impressions_count} · ratio {(Number(item.dislike_ratio) * 100).toFixed(0)}%
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => restoreCommunityMutation.mutate({
                                            contentType: item.content_type,
                                            contentId: item.content_id,
                                        })}
                                        disabled={restoreCommunityMutation.isPending}
                                        className="self-center px-3 py-1.5 rounded-lg text-xs font-medium bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors"
                                    >
                                        Restaurar
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )
            ) : (
            <>
            {/* Stats Dashboard */}
            {!isLoadingStats && stats && <StatsGrid stats={stats} />}
            {isLoadingStats && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="h-[76px] rounded-xl border border-border bg-card animate-pulse" />
                    ))}
                </div>
            )}

            {/* Status Filter + Resolve all */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex gap-1 p-1 bg-muted/50 rounded-lg w-fit">
                    {STATUS_FILTERS.map(f => (
                        <button
                            key={f.value}
                            onClick={() => setStatusFilter(f.value)}
                            className={cn(
                                'px-4 py-1.5 rounded-md text-sm font-medium transition-all relative',
                                statusFilter === f.value
                                    ? 'bg-primary text-primary-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                            )}
                        >
                            {f.label}
                            {f.count !== undefined && f.count > 0 && (
                                <span className={cn(
                                    'ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold',
                                    statusFilter === f.value
                                        ? 'bg-primary-foreground/20 text-primary-foreground'
                                        : 'bg-amber-500/20 text-amber-400'
                                )}>
                                    {f.count}
                                </span>
                            )}
                        </button>
                    ))}
                </div>
                {pendingCount > 0 && (
                    <button
                        type="button"
                        onClick={() => {
                            setAdminNotes('');
                            setConfirmResolveAll(true);
                        }}
                        disabled={resolveAllMutation.isPending}
                        className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 border border-emerald-500/30 transition-colors disabled:opacity-50"
                    >
                        <CheckCircle size={14} />
                        Resolver todos
                        <span className="text-xs opacity-80">({pendingCount})</span>
                    </button>
                )}
            </div>

            {/* Reports List */}
            {isLoading ? (
                <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="rounded-xl border border-border bg-card animate-pulse">
                            <div className="flex items-center gap-4 p-5">
                                <div className="w-10 h-10 rounded-lg bg-muted/50" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 bg-muted/50 rounded w-1/3" />
                                    <div className="h-3 bg-muted/30 rounded w-2/3" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : !reports || reports.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border bg-card/50 py-16 text-center">
                    <Shield size={32} className="mx-auto text-muted-foreground/40 mb-3" />
                    <p className="text-sm font-medium text-muted-foreground">
                        {statusFilter === 'pending' ? 'No hay reportes pendientes' : 'No hay reportes'}
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-1">
                        ¡Todo bajo control!
                    </p>
                </div>
            ) : (
                <div className="space-y-4">
                    {reports.map(report => (
                        <ReportCard
                            key={report.id}
                            report={report}
                            isExpanded={expandedId === report.id}
                            onToggle={() => setExpandedId(expandedId === report.id ? null : report.id)}
                            onDismiss={() => handleDismiss(report)}
                            onResolve={() => handleResolve(report)}
                            isActioning={dismissMutation.isPending || resolveMutation.isPending}
                        />
                    ))}
                </div>
            )}
            </>
            )}

            {/* Confirm Dialog */}
            {confirmAction && (
                <ConfirmAction
                    title={
                        confirmAction.type === 'dismiss'
                            ? 'Descartar reporte'
                            : 'Eliminar contenido'
                    }
                    description={
                        confirmAction.type === 'dismiss'
                            ? 'Este reporte se marcará como descartado. El contenido no será eliminado.'
                            : `Se eliminará permanentemente el contenido reportado (${CONTENT_TYPE_LABELS[confirmAction.report.content_type] || confirmAction.report.content_type}) y el reporte se marcará como resuelto. Esta acción no se puede deshacer.`
                    }
                    confirmLabel={
                        confirmAction.type === 'dismiss' ? 'Descartar' : 'Eliminar contenido'
                    }
                    confirmVariant={confirmAction.type === 'resolve' ? 'danger' : 'neutral'}
                    onConfirm={executeConfirmAction}
                    onCancel={() => setConfirmAction(null)}
                    isLoading={dismissMutation.isPending || resolveMutation.isPending}
                    showNotesInput={true}
                    notes={adminNotes}
                    onNotesChange={setAdminNotes}
                />
            )}

            {confirmResolveAll && (
                <ConfirmAction
                    title="Resolver todos los pendientes"
                    description={`Se marcarán como resueltos ${pendingCount} reporte${pendingCount === 1 ? '' : 's'} pendiente${pendingCount === 1 ? '' : 's'}. El contenido no se eliminará; solo saldrán de Pendientes.`}
                    confirmLabel="Resolver todos"
                    confirmVariant="neutral"
                    onConfirm={() => {
                        resolveAllMutation.mutate(adminNotes || undefined, {
                            onSuccess: () => {
                                setConfirmResolveAll(false);
                                setStatusFilter('pending');
                            },
                        });
                    }}
                    onCancel={() => setConfirmResolveAll(false)}
                    isLoading={resolveAllMutation.isPending}
                    showNotesInput={true}
                    notes={adminNotes}
                    onNotesChange={setAdminNotes}
                />
            )}
        </div>
    );
}

// ─── Report Card ────────────────────────────────────────────────────────────

function ReportCard({ report, isExpanded, onToggle, onDismiss, onResolve, isActioning }: {
    report: EnrichedContentReport;
    isExpanded: boolean;
    onToggle: () => void;
    onDismiss: () => void;
    onResolve: () => void;
    isActioning: boolean;
}) {
    const statusCfg = STATUS_CONFIG[report.status] || STATUS_CONFIG.pending;
    const createdDate = new Date(report.created_at);
    const isActionable = report.status === 'pending' || report.status === 'reviewing';
    const ContentIcon = CONTENT_TYPE_ICONS[report.content_type] || Flag;
    const reasonColor = REASON_COLORS[report.reason] || REASON_COLORS.other;
    const imageUrl = resolveImageUrl(report.content_image);
    const hasMultipleReports = report.total_reports_for_content > 1;

    return (
        <div className={cn(
            'rounded-xl border bg-card transition-all overflow-hidden',
            report.status === 'pending' ? 'border-amber-500/30' : 'border-border',
            isExpanded && 'ring-1 ring-primary/20'
        )}>
            {/* Header Row — always visible */}
            <div
                className="flex items-stretch cursor-pointer hover:bg-muted/20 transition-colors"
                onClick={onToggle}
            >
                {/* Thumbnail (captures/moments) */}
                {imageUrl && !report.content_type.startsWith('comment_') ? (
                    <div className="w-20 h-20 lg:w-24 lg:h-24 shrink-0 bg-black/20 overflow-hidden">
                        <img
                            src={imageUrl}
                            alt=""
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                                (e.target as HTMLImageElement).parentElement!.innerHTML = `
                                    <div class="w-full h-full flex items-center justify-center bg-muted/30">
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-muted-foreground/40"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="m21 15-5-5L5 21"/></svg>
                                    </div>
                                `;
                            }}
                        />
                    </div>
                ) : (
                    <div className={cn(
                        'w-20 h-20 lg:w-24 lg:h-24 shrink-0 flex items-center justify-center',
                        report.status === 'pending' ? 'bg-amber-500/5' : 'bg-muted/20'
                    )}>
                        <ContentIcon size={24} className={cn(
                            report.status === 'pending' ? 'text-amber-500/60' : 'text-muted-foreground/30'
                        )} />
                    </div>
                )}

                {/* Main Info */}
                <div className="flex-1 min-w-0 px-4 py-3 flex flex-col justify-center">
                    {/* Top line: type + status + report count */}
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-sm font-semibold text-foreground">
                            {CONTENT_TYPE_LABELS[report.content_type] || report.content_type}
                        </span>
                        <StatusBadge status={statusCfg.label} variant={statusCfg.variant} />
                        {hasMultipleReports && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-500/10 text-red-400 text-[10px] font-bold">
                                <Megaphone size={9} />
                                {report.total_reports_for_content} reportes
                            </span>
                        )}
                        {!report.content_exists && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-muted/50 text-muted-foreground text-[10px] font-medium">
                                Eliminado
                            </span>
                        )}
                    </div>

                    {/* Reason badge */}
                    <div className="flex items-center gap-2 mb-1">
                        <span className={cn(
                            'inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border',
                            reasonColor
                        )}>
                            <AlertTriangle size={10} />
                            {REASON_LABELS[report.reason] || report.reason}
                        </span>
                    </div>

                    {/* Report description / message */}
                    {report.description && (
                        <p className="text-sm text-foreground/80 line-clamp-1 mt-0.5">
                            "{report.description}"
                        </p>
                    )}

                    {/* Bottom line: reporter + time */}
                    <p className="text-xs text-muted-foreground mt-1">
                        Reportado por <span className="font-medium">{report.reporter_display_name || report.reporter_email || 'Anónimo'}</span>
                        {' · '}
                        {formatDistanceToNow(createdDate, { addSuffix: true, locale: es })}
                    </p>
                </div>

                {/* Expand arrow */}
                <div className="flex items-center px-3 shrink-0">
                    <ChevronDown
                        size={16}
                        className={cn(
                            'text-muted-foreground transition-transform',
                            isExpanded && 'rotate-180'
                        )}
                    />
                </div>
            </div>

            {/* Expanded Details */}
            {isExpanded && (
                <div className="border-t border-border/50 animate-in slide-in-from-top-2 duration-200">
                    <div className="p-5 space-y-5">
                        {/* ─── Content Preview ─── */}
                        <div>
                            <h4 className="text-xs font-semibold text-muted-foreground/70 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <Eye size={12} />
                                Contenido reportado
                            </h4>
                            <ContentPreview report={report} />
                        </div>

                        {/* ─── Report Message ─── */}
                        {report.description && (
                            <div className="rounded-lg bg-amber-500/5 border border-amber-500/10 p-4">
                                <h4 className="text-xs font-semibold text-amber-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                                    <MessageSquare size={12} />
                                    Mensaje del reporte
                                </h4>
                                <p className="text-sm text-foreground leading-relaxed">
                                    {report.description}
                                </p>
                            </div>
                        )}

                        {/* ─── Users Section ─── */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Reporter */}
                            <div className="rounded-lg bg-muted/20 border border-border/50 p-3">
                                <UserInfo
                                    label="Reportado por"
                                    name={report.reporter_display_name}
                                    email={report.reporter_email}
                                    avatarUrl={report.reporter_avatar_url}
                                    userId={report.reporter_id}
                                    variant="reporter"
                                />
                            </div>

                            {/* Content Author */}
                            <div className="rounded-lg bg-muted/20 border border-border/50 p-3">
                                <UserInfo
                                    label="Autor del contenido"
                                    name={report.content_author_name}
                                    avatarUrl={report.content_author_avatar}
                                    userId={report.content_author_id}
                                    variant="author"
                                />
                            </div>
                        </div>

                        {/* ─── Admin Notes ─── */}
                        {report.admin_notes && (
                            <div className="rounded-lg bg-blue-500/5 border border-blue-500/10 p-4">
                                <h4 className="text-xs font-semibold text-blue-400 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                                    <Info size={12} />
                                    Notas del administrador
                                </h4>
                                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                                    {report.admin_notes}
                                </p>
                            </div>
                        )}

                        {/* ─── Metadata Grid ─── */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
                            <div className="rounded-lg bg-muted/10 p-2.5">
                                <p className="text-muted-foreground/50 uppercase tracking-wider font-semibold mb-0.5">
                                    ID Reporte
                                </p>
                                <p className="text-foreground font-mono text-[11px]">
                                    {report.id.substring(0, 12)}...
                                </p>
                            </div>
                            <div className="rounded-lg bg-muted/10 p-2.5">
                                <p className="text-muted-foreground/50 uppercase tracking-wider font-semibold mb-0.5">
                                    ID Contenido
                                </p>
                                <p className="text-foreground font-mono text-[11px]">
                                    {report.content_id.substring(0, 12)}...
                                </p>
                            </div>
                            <div className="rounded-lg bg-muted/10 p-2.5">
                                <p className="text-muted-foreground/50 uppercase tracking-wider font-semibold mb-0.5">
                                    Fecha reporte
                                </p>
                                <p className="text-foreground text-[11px]">
                                    {format(createdDate, "d 'de' MMMM yyyy", { locale: es })}
                                </p>
                                <p className="text-muted-foreground/40 text-[10px]">
                                    {format(createdDate, "HH:mm", { locale: es })}
                                </p>
                            </div>
                            <div className="rounded-lg bg-muted/10 p-2.5">
                                <p className="text-muted-foreground/50 uppercase tracking-wider font-semibold mb-0.5">
                                    Total reportes
                                </p>
                                <p className={cn(
                                    'font-bold text-[11px]',
                                    hasMultipleReports ? 'text-red-400' : 'text-foreground'
                                )}>
                                    {report.total_reports_for_content} {report.total_reports_for_content === 1 ? 'reporte' : 'reportes'} para este contenido
                                </p>
                            </div>
                        </div>

                        {/* ─── Actions ─── */}
                        {isActionable && (
                            <div className="flex items-center gap-3 pt-2 border-t border-border/30">
                                <button
                                    onClick={(e) => { e.stopPropagation(); onDismiss(); }}
                                    disabled={isActioning}
                                    className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors disabled:opacity-50"
                                >
                                    <XCircle size={14} />
                                    Descartar reporte
                                </button>
                                {report.content_exists && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); onResolve(); }}
                                        disabled={isActioning}
                                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors disabled:opacity-50"
                                    >
                                        <Trash2 size={14} />
                                        Eliminar contenido
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
